import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/design-system/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/design-system/popover';
import { SearchInput } from '@/components/ui/SearchInput';
import { CloseOutlined, TableOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { ColumnPickerList } from './ColumnPickerList';

export interface ColumnDefinition {
  id: string;
  label: string;
  defaultVisible?: boolean;
  canHide?: boolean;
}

interface ColumnPickerProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onVisibilityChange: (visibleColumnIds: string[]) => void;
  showLabel?: boolean;
  'data-testid'?: string;
}

export const ColumnPicker = ({
  columns,
  visibleColumns,
  onVisibilityChange,
  showLabel = true,
  'data-testid': dataTestId = 'column-picker'
}: ColumnPickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const hideableColumns = useMemo(() => columns.filter((col) => col.canHide !== false), [columns]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return hideableColumns;
    const query = searchQuery.toLowerCase();
    return hideableColumns.filter((col) => col.label.toLowerCase().includes(query));
  }, [hideableColumns, searchQuery]);

  const visibleCount = useMemo(
    () => hideableColumns.filter((col) => visibleColumns.includes(col.id)).length,
    [hideableColumns, visibleColumns]
  );

  const handleToggleColumn = useCallback(
    (columnId: string, checked: boolean) => {
      onVisibilityChange(checked ? [...visibleColumns, columnId] : visibleColumns.filter((id) => id !== columnId));
    },
    [visibleColumns, onVisibilityChange]
  );

  const handleShowAll = useCallback(() => {
    const allHideableIds = hideableColumns.map((col) => col.id);
    const nonHideableIds = columns.filter((col) => col.canHide === false).map((col) => col.id);
    onVisibilityChange([...nonHideableIds, ...allHideableIds]);
  }, [columns, hideableColumns, onVisibilityChange]);

  const handleResetToDefault = useCallback(() => {
    onVisibilityChange(columns.filter((col) => col.defaultVisible !== false).map((col) => col.id));
  }, [columns, onVisibilityChange]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="border-border-100"
          title={
            !showLabel
              ? `${t('grid.columnPicker.columns')} (${String(visibleCount)}/${String(hideableColumns.length)})`
              : undefined
          }
          data-testid={dataTestId}
        >
          <TableOutlined />
          {showLabel && (
            <>
              {t('grid.columnPicker.columns')}
              <span className="font-normal opacity-60">
                ({visibleCount}/{hideableColumns.length})
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0">
        <div className="border-border flex items-center justify-between border-b p-3">
          <span className="text-typography-900 text-sm font-medium">{t('grid.columnPicker.toggleColumns')}</span>
          <Button
            variant="ghost"
            size="xs"
            className="h-auto w-auto min-w-0 p-0.5"
            onClick={handleClose}
            data-testid={`${dataTestId}-close`}
          >
            <CloseOutlined />
          </Button>
        </div>

        <div className="border-border border-b p-3">
          <SearchInput
            size="sm"
            placeholder={t('grid.columnPicker.searchPlaceholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            data-testid={`${dataTestId}-search`}
          />
        </div>

        <ColumnPickerList
          columns={filteredColumns}
          visibleColumns={visibleColumns}
          onToggle={handleToggleColumn}
          dataTestId={dataTestId}
        />

        <div className="border-border flex items-center gap-2 border-t p-3">
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleShowAll}
            data-testid={`${dataTestId}-show-all`}
          >
            {t('grid.columnPicker.showAll')}
          </Button>
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleResetToDefault}
            data-testid={`${dataTestId}-reset`}
          >
            {t('grid.columnPicker.resetToDefault')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
